import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MonoChip } from '@/features/agents/components/MonoChip';
import { SubsetDeniedAlert } from '@/features/agents/components/SubsetDeniedAlert';

import { kindLabel } from '../labels';
import type { ModelCollection, SubsetDenial, TemplateWizardStep, WizardDraft } from '../types';

type ReviewTemplate = {
  name: string;
  description?: string;
  kind: string;
  policySpecs: {
    name: string;
    statements: { effect: string; actions: string[]; resources: string[] }[];
  }[];
  toolConfig?: Record<string, unknown>;
};

export interface ReviewStepProps {
  template: ReviewTemplate;
  draft: WizardDraft;
  modelCollection?: ModelCollection;
  /** Required template steps without a registered renderer (§16). */
  missingRequired: TemplateWizardStep[];
  createError: { denial: SubsetDenial | null; message?: string } | null;
  onEditBasics: () => void;
  /** Generic text/select step answers (advisory, §10.3/§25 Q3). */
  advisoryRows: { title: string; value: string }[];
}

/**
 * Frame step 3 — review (design §15.1): definition-list setup rows, the FULL
 * permissions display (every template `policySpecs` statement + the user's
 * added grants — "no grant is minted that isn't shown here", audit #4 trust
 * display), the required-but-unavailable warning, and the create-error surface
 * (`SubsetDeniedAlert` / verbatim backend message) with all draft data intact
 * (E1). The Create button itself lives in the wizard footer (§7.2).
 */
export function ReviewStep({
  template,
  draft,
  modelCollection,
  missingRequired,
  createError,
  onEditBasics,
  advisoryRows,
}: ReviewStepProps) {
  const [policiesExpanded, setPoliciesExpanded] = useState(false);

  const grantCount = draft.policies.statements.length;
  const triggerCount = draft.triggers.items.length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Check everything before creating the agent.</p>

      {missingRequired.map((step) => (
        <div
          key={step.id}
          role="alert"
          className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-card p-3"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" aria-hidden="true" />
          <p className="text-sm">“{step.title}” is required and isn't available yet.</p>
        </div>
      ))}

      {createError ? (
        createError.denial ? (
          <SubsetDeniedAlert denial={createError.denial} />
        ) : (
          <div
            role="alert"
            className="space-y-1 rounded-md border-l-2 border-l-destructive bg-card p-4"
          >
            <p className="flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              Couldn't create this agent.
            </p>
            {createError.message ? (
              <p className="text-sm text-muted-foreground">{createError.message}</p>
            ) : null}
            <p className="text-sm text-muted-foreground">Please try again.</p>
          </div>
        )
      ) : null}

      <div className="space-y-2 rounded-lg border bg-card p-4">
        {/* `<div>` not `<p>`: Badge renders a div — validateDOMNesting (§22). */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="rounded-sm">
            {kindLabel(template.kind)}
          </Badge>
          <span className="text-sm font-medium">{template.name}</span>
        </div>
        {template.description ? (
          <p className="text-sm text-muted-foreground">{template.description}</p>
        ) : null}
      </div>

      <dl className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap justify-between gap-2">
          <dt className="text-sm text-muted-foreground">Name</dt>
          <dd className="text-sm">{draft.basics.name || '—'}</dd>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <dt className="text-sm text-muted-foreground">Model</dt>
          <dd className="text-sm">{modelCollection?.name ?? '—'}</dd>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <dt className="text-sm text-muted-foreground">Memory</dt>
          <dd className="text-sm">
            {draft.memory.attach ? 'On — read and write its own collection' : 'Off'}
          </dd>
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-sm text-muted-foreground">Instructions</dt>
            <dd className="text-sm">
              <span className="inline-flex max-w-md flex-col items-end gap-1">
                <span className="line-clamp-3 text-right">{draft.basics.instructions || '—'}</span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={onEditBasics}
                >
                  Edit
                </Button>
              </span>
            </dd>
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <dt className="text-sm text-muted-foreground">Extra policies</dt>
            <dd className="text-sm">
              {grantCount === 0 ? (
                'None'
              ) : (
                <button
                  type="button"
                  className="inline-flex items-center gap-1"
                  aria-expanded={policiesExpanded}
                  onClick={() => setPoliciesExpanded((open) => !open)}
                >
                  {grantCount} grants
                  {policiesExpanded ? (
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              )}
            </dd>
          </div>
          {policiesExpanded && (
            <ul className="space-y-1">
              {draft.policies.statements.map((statement, index) => (
                <li key={index} className="flex flex-wrap items-center gap-1.5 text-sm">
                  <span className="text-muted-foreground">{statement.effect}</span>
                  <MonoChip>{statement.action}</MonoChip>
                  <span className="text-muted-foreground">on</span>
                  <MonoChip>{statement.resource}</MonoChip>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <dt className="text-sm text-muted-foreground">Triggers</dt>
          <dd className="text-sm">
            {triggerCount === 0
              ? 'None'
              : draft.triggers.items
                  .map((item) =>
                    item.kind === 'cron' ? `Cron ${item.cron ?? ''}`.trim() : item.kind,
                  )
                  .join(', ')}
          </dd>
        </div>
      </dl>

      {advisoryRows.length > 0 && (
        <div className="space-y-2 rounded-lg border bg-card p-4">
          <p className="text-sm font-medium">Additional answers</p>
          <dl className="space-y-1">
            {advisoryRows.map((row, index) => (
              <div key={index} className="flex flex-wrap justify-between gap-2">
                <dt className="text-sm text-muted-foreground">{row.title}</dt>
                <dd className="text-sm">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <p className="text-sm font-medium">Permissions this agent will receive</p>

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">From the template (minted at create):</p>
          {template.policySpecs.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="space-y-1">
              {template.policySpecs.map((spec, specIndex) => (
                <li key={`${spec.name}-${specIndex}`} className="space-y-1">
                  <p className="text-sm">{spec.name}</p>
                  <ul className="space-y-1 pl-3">
                    {spec.statements.map((statement, index) => (
                      <li key={index} className="flex flex-wrap items-center gap-1.5 text-sm">
                        <span className="text-muted-foreground">{statement.effect}</span>
                        {statement.actions.map((action) => (
                          <MonoChip key={`a-${action}`}>{action}</MonoChip>
                        ))}
                        <span className="text-muted-foreground">on</span>
                        {statement.resources.map((resource) => (
                          <MonoChip key={`r-${resource}`}>{resource}</MonoChip>
                        ))}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          <p className="text-sm">
            {grantCount === 0 ? 'From you: none' : `From you: ${grantCount} grants`}
          </p>
        </div>

        {Object.keys(template.toolConfig ?? {}).length > 0 && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Tool surface (read-only):</p>
            <p className="flex flex-wrap gap-1.5">
              {Object.keys(template.toolConfig ?? {}).map((key) => (
                <MonoChip key={key}>{key}</MonoChip>
              ))}
            </p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Creating grants these permissions — you can only pass on what you already hold. If a check
          fails, creation stops and lists what's missing.
        </p>
      </div>
    </div>
  );
}
